/**
 * Dual-host (NotebookLM-style) helpers for help narrations.
 *
 * Voices are fixed: Clara (female, en-CA-ClaraNeural) and Liam (male, en-CA-LiamNeural).
 *
 * Preferred script shape — explicit turns:
 *   { title, icon?, turns: [{ speaker: 'clara'|'liam', speech }] }
 *
 * Legacy shape still works — monologue `speech` is auto-split into alternating
 * host turns so every current help gets two voices without a rewrite:
 *   { title, icon?, speech, speaker? }
 */

export const HOSTS = {
  clara: { id: 'clara', label: 'Clara', gender: 'female' },
  liam: { id: 'liam', label: 'Liam', gender: 'male' },
};

export function normalizeSpeaker(raw) {
  const s = String(raw || '').toLowerCase().trim();
  if (s === 'liam' || s === 'male' || s === 'm') return 'liam';
  return 'clara';
}

export function speakerGender(speaker) {
  return normalizeSpeaker(speaker) === 'liam' ? 'male' : 'female';
}

export function speakerLabel(speaker) {
  return HOSTS[normalizeSpeaker(speaker)].label;
}

const splitSentences = (text) => (String(text || '').match(/[^.!?]+[.!?]+["']?\s*/g) || (String(text || '').trim() ? [text] : []))
  .map((s) => s.trim())
  .filter(Boolean);

/**
 * Expand a help script into dual-host turns.
 * Returns [{ title, icon, turns: [{ speaker, speech }] }].
 */
export function expandDualHost(sections) {
  if (!Array.isArray(sections)) return [];

  return sections.map((section, sectionIdx) => {
    const title = section?.title || `Part ${sectionIdx + 1}`;
    const icon = section?.icon;

    if (Array.isArray(section?.turns) && section.turns.length) {
      return {
        title,
        icon,
        turns: section.turns
          .map((t) => ({
            speaker: normalizeSpeaker(t.speaker),
            speech: String(t.speech || '').replace(/\s+/g, ' ').trim(),
          }))
          .filter((t) => t.speech),
      };
    }

    const sentences = splitSentences(section?.speech);
    const start = section?.speaker
      ? normalizeSpeaker(section.speaker)
      : (sectionIdx % 2 === 0 ? 'clara' : 'liam');

    // Explicit single-host section (rare): keep as one turn.
    if (section?.speaker && !section?.dual) {
      return {
        title,
        icon,
        turns: [{
          speaker: start,
          speech: sentences.join(' ') || String(section.speech || '').trim(),
        }].filter((t) => t.speech),
      };
    }

    // Auto dual: alternate hosts every sentence (NotebookLM rhythm).
    // Section start host flips each section so neither always opens.
    const turns = [];
    let who = start;
    sentences.forEach((sentence) => {
      turns.push({ speaker: who, speech: sentence });
      who = who === 'clara' ? 'liam' : 'clara';
    });

    if (!turns.length && section?.speech) {
      turns.push({ speaker: start, speech: String(section.speech).replace(/\s+/g, ' ').trim() });
    }

    return { title, icon, turns };
  }).filter((s) => s.turns.length > 0);
}

/**
 * Flatten expanded sections into a playback queue.
 * hostMode: 'duo' | 'clara' | 'liam'
 */
export function buildDualHostQueue(sections, hostMode = 'duo') {
  const expanded = expandDualHost(sections);
  const q = [];
  expanded.forEach((section, sectionIdx) => {
    section.turns.forEach((turn) => {
      let speaker = turn.speaker;
      if (hostMode === 'clara') speaker = 'clara';
      if (hostMode === 'liam') speaker = 'liam';
      splitSentences(turn.speech).forEach((sentence) => {
        q.push({
          sentence,
          sectionIdx,
          speaker,
          gender: speakerGender(speaker),
        });
      });
    });
  });
  return { sections: expanded, queue: q };
}
